import { Component } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { PageEvent } from '@angular/material/paginator';
import { AuthService, BreadcrumbService, ProductNameService, StateService } from 'src/app/services';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { ViewControls } from 'src/app/shared/models/view-controls';
import { ProductName, RenderedOutputStatus } from 'situview-webapi-client';
import { ListRecipeEventSummariesRequest, ListRecipeEventSummariesResponse, RecipeEventSummary, RecipeStatus, SituAnalyticsWebApiClient } from 'situanalytics-webapi-client';
import { ActivatedRoute, Route, Router } from '@angular/router';
import { HomeStatus } from 'siturenderservice-webapi-client';

@Component({
  selector: 'recipe-event-summary-list',
  templateUrl: './recipe-event-summary-list.component.html',
  styleUrls: ['./recipe-event-summary-list.component.scss']
})
export class RecipeEventSummaryListComponent extends SituBaseComponent {
  private readonly DefaultPageSize: number = 25;
  private readonly DefaultPageIndex: number = 0;
  readonly FilterParamName: string = "filter";
  readonly ProductParamName: string = "product";
  readonly StatusParamName: string = "status";
  private readonly PageSizeParamName: string = "pageSize";
  private readonly PageIndexParamName: string = "pageIndex";

  viewControls = new ViewControls(this.DefaultPageSize, this.DefaultPageIndex, 0, [this.DefaultPageSize, 50, 100]);

  private _loading = false;
  public get loading(): boolean {
    return this._loading;
  }

  public set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  productName = ProductName;
  statusName = RecipeStatus;
  status = RenderedOutputStatus;

  productNameFilter: ProductName | undefined;
  statusNameFilter: RecipeStatus | undefined;
  companyFilter: boolean = false;
  previousSearchTerm = "";
  searchTerm = "";
  dataSource = new MatTableDataSource<RecipeEventSummary>();
  inProgressSummaries: RecipeEventSummary[] | undefined;
  queuedSummaries: RecipeEventSummary[] | undefined;
  errorSummaries: RecipeEventSummary[] | undefined;
  completeSummaries: RecipeEventSummary[] | undefined;

  constructor(
    authService: AuthService,
    stateService: StateService,
    private breadcrumbService: BreadcrumbService,
    private situAnalyticsClient: SituAnalyticsWebApiClient,
    private productNameService: ProductNameService,
    private route: ActivatedRoute,
    private router: Router) {
      super(authService, stateService);
      if (this.route.snapshot.queryParams[this.FilterParamName]) {
        this.searchTerm = this.route.snapshot.queryParams[this.FilterParamName];
      }
      if (this.route.snapshot.queryParams[this.StatusParamName]) {
        let numberValue = parseInt(this.route.snapshot.queryParams[this.StatusParamName]);
        if (!isNaN(numberValue)) {
          this.statusNameFilter = numberValue;
        }
      }
      if (this.route.snapshot.queryParams[this.ProductParamName]) {
        let numberValue = parseInt(this.route.snapshot.queryParams[this.ProductParamName]);
        if (!isNaN(numberValue)) {
          this.productNameFilter = numberValue;
        }
      }
      if (this.route.snapshot.queryParams[this.PageSizeParamName]) {
        let numberValue = parseInt(this.route.snapshot.queryParams[this.PageSizeParamName]);
        if (!isNaN(numberValue)) {
          this.viewControls.pageSize = numberValue;
        }
      }
      if (this.route.snapshot.queryParams[this.PageIndexParamName]) {
        let numberValue = parseInt(this.route.snapshot.queryParams[this.PageIndexParamName]);
        if (!isNaN(numberValue)) {
          this.viewControls.pageIndex = numberValue;
        }
      }
  }

  protected onSelectedCompanyChange(): void {
    this.loadRecipeEventSummaries();
  }

  clearFilter(filter: string) {
    switch(filter) {
      case this.FilterParamName:
        this.searchTerm = "";
        break;
      case this.StatusParamName:
        this.statusNameFilter = undefined;
        break;
      case this.ProductParamName:
        this.productNameFilter = undefined;
        break;
    }
    this.updateParams();
    this.loadRecipeEventSummaries();
  }

  setProductFilter(productName: ProductName) {
    this.productNameFilter = productName;
    this.updateParams();
    this.loadRecipeEventSummaries();
  }

  filterStatus(status: RecipeStatus) {
    this.statusNameFilter = status;
    this.updateParams();
    this.loadRecipeEventSummaries();
  }

  private updateParams() {
    const queryParams: { [key: string]: any } = {};

    if (this.searchTerm) {
      queryParams.filter = this.searchTerm;
    }
    if (this.statusNameFilter !== undefined) {
      queryParams.status = this.statusNameFilter;
    }
    if (this.productNameFilter !== undefined) {
      queryParams.product = this.productNameFilter;
    }
    if (this.viewControls.pageSize) {
      queryParams.pageSize = this.viewControls.pageSize;
    }
    if (this.viewControls.pageIndex) {
      queryParams.pageIndex = this.viewControls.pageIndex;
    }
    
    const urlTree = this.router.createUrlTree([], {
      queryParamsHandling: "merge",
      preserveFragment: true,
      queryParams: queryParams
    });

    if (!this.searchTerm) {
      delete urlTree.queryParams[this.FilterParamName];
    }
    if (!(this.statusNameFilter !== undefined)) {
      delete urlTree.queryParams[this.StatusParamName];
    }
    if (!(this.productNameFilter !== undefined)) {
      delete urlTree.queryParams[this.ProductParamName];
    }
    if (this.viewControls.pageSize === this.DefaultPageSize) {
      delete urlTree.queryParams[this.PageSizeParamName];
    }
    if (this.viewControls.pageIndex === this.DefaultPageIndex) {
      delete urlTree.queryParams[this.PageIndexParamName];
    }
  
    this.router.navigateByUrl(urlTree);
  }

  loadRecipeEventSummaries() {
    this.loading = true;
    let request = new ListRecipeEventSummariesRequest();
    if (this.companyFilter) request.companyId = this.selectedCompany?.id;
    request.pageNumber = this.viewControls.pageIndex + 1;
    request.pageSize = this.viewControls.pageSize;
    if (this.searchTerm) request.containsWords = [this.searchTerm];
    if (this.productNameFilter || this.productNameFilter === 0) request.productName = this.productNameFilter;
    if (this.statusNameFilter || this.statusNameFilter === 0) request.recipeStatus = this.statusNameFilter;

    this.situAnalyticsClient.listRecipeEventSummaries(request)
      .then((response: ListRecipeEventSummariesResponse) => {
        if (response.success && response?.items) {
          this.dataSource.data = response.items;
          this.viewControls.totalItems = response.totalItems ?? 0;
          this.updateParams();
          this.distributeSummaries();
        }
      })
      .catch((x: any) => {
        //this.showMessage("Sorry! There was an issue at our end retrieving your renders. Please try again.");
      })
      .finally(() => {
        this.loading = false;
      });
  }

  toggleCompanyFilter() {
    this.companyFilter = !this.companyFilter;
    this.loadRecipeEventSummaries();
  }

  distributeSummaries() {
    if (this.dataSource.data) {
      this.queuedSummaries = [];
      this.inProgressSummaries = [];
      this.errorSummaries = [];
      this.completeSummaries = [];
      this.dataSource.data.forEach((recipeEventSummary: RecipeEventSummary) => {
        const isInProgress = !recipeEventSummary.dateCancelled && !recipeEventSummary.dateFailed
        && !recipeEventSummary.dateCompleted && !!recipeEventSummary.dateStarted;
        if (isInProgress) {
          this.inProgressSummaries?.push(recipeEventSummary);
          return;
        }
  
        if (!!recipeEventSummary.dateCompleted) {
          this.completeSummaries?.push(recipeEventSummary);
          return;
        }
  
        if (!!recipeEventSummary.dateFailed || !!recipeEventSummary.dateCancelled) {
          this.errorSummaries?.push(recipeEventSummary);
          return;
        }
  
        if (!!recipeEventSummary.dateQueued) {
          this.queuedSummaries?.push(recipeEventSummary);
          return;
        }
      });
    }
  }

  getStatusIconAndClass(recipeEventSummary: RecipeEventSummary) {
    // in progress
    const isInProgress = !recipeEventSummary.dateCancelled && !recipeEventSummary.dateFailed
      && !recipeEventSummary.dateCompleted && !!recipeEventSummary.dateStarted;
    if (isInProgress) {
      return { icon: "pending", class: "text-info", description: "In Progress" };
    }

    // completed
    if (!!recipeEventSummary.dateCompleted) {
      return { icon: "check_circle", class: "text-success", description: "Completed" };
    }

    // failed
    if (!!recipeEventSummary.dateFailed) {
      return { icon: "error", class: "text-danger", description: "Failed" };
    }

    // cancelled
    if (!!recipeEventSummary.dateCancelled) {
      return { icon: "block", class: "text-muted", description: "Cancelled" };
    }

    // queued
    if (!!recipeEventSummary.dateQueued) {
      return { icon: "pending", class: "text-muted", description: "Queued" };
    }
  }

  getProductColour(productName: ProductName) {
    return this.productNameService.getProductNameConfiguration(productName as any)?.productColor;
  }

  /** The product's display name (e.g. situDAYDREAM), falling back to the enum name. */
  getProductLabel(productName: ProductName | undefined): string {
    if (productName === undefined) return '';
    return this.productNameService.getProductNameConfiguration(productName as any)?.product ?? ProductName[productName];
  }

  onPageChange(event: PageEvent) {
    this.viewControls.pageSize = event.pageSize;
    this.viewControls.pageIndex = event.pageIndex;

    this.loadRecipeEventSummaries();
  }

  resetSearch() {
    this.viewControls.pageIndex = 0;
    this.viewControls.pageSize = 25;
    this.viewControls.totalItems = 0;
  }

  isOlderThan2Minutes(date: Date) {
    const twoMinutesAgo = new Date(Date.now() - 2 * 60 * 1000); // 2 minutes in milliseconds
    return date < twoMinutesAgo;
  }

  private isFromYesterday(date: Date) {
    const now = new Date();
    const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
    return date < yesterday;
  }

  private searchTermChanged(): boolean {
    return this.searchTerm.toLowerCase() !== this.previousSearchTerm.toLowerCase();
  }
}
